/**
 * Remote template storage operations using storage service
 */

import storageService from "../../data/Services/storageService.jsx";
import logger from "../../utils/logger";
import {
  serializeTemplate,
  deserializeTemplate,
} from "../core/templateSerialization.js";

/**
 * Save template remotely to marketdata service
 * @param {Object} chart - The chart to save as template
 * @param {string} name - Template name
 * @param {Object} options - Save options
 * @param {string} options.id - Template ID (for updates)
 * @returns {Promise<Object>} - Saved template data with id
 */
export const saveTemplateRemote = async (chart, name, options = {}) => {
  try {
    if (!chart) {
      throw new Error("Chart is required");
    }

    if (!name || name.trim() === "") {
      throw new Error("Template name is required");
    }

    // Serialize the template
    const templateData = serializeTemplate(chart);
    const content = JSON.stringify(templateData);

    // Save to remote service
    // Server expects: -1 for new template, or existing chartid for update
    const chartId =
      options.id !== undefined && options.id !== null ? options.id : -1;

    const savedData = await storageService.saveTemplate(
      chartId,
      name.trim(),
      content,
    );

    logger.workspace.success(`Template "${name}" salvo no servidor`, {
      name: name,
      id: savedData?.chartid || savedData || chartId,
    });

    return {
      id: savedData?.chartid || savedData || chartId,
      name: name.trim(),
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Failed to save template remotely:", error);
    logger.workspace.error(`Falha ao salvar template "${name}"`, {
      error: error.message,
      name: name,
    });
    throw error;
  }
};

/**
 * Load template from remote storage and apply to chart
 * @param {string} id - Template ID
 * @param {Object} chartStore - Chart store instance
 * @param {string} chartId - Target chart ID to apply template to
 * @returns {Promise<boolean>} - Success status
 */
export const loadTemplateRemote = async (id, chartStore, chartId) => {
  try {
    if (!id) {
      throw new Error("Template ID is required");
    }

    if (!chartStore) {
      throw new Error("Chart store is required");
    }

    if (!chartId) {
      throw new Error("Chart ID is required");
    }

    console.log("Loading template from remote:", id);

    // Fetch template data from service
    const remoteData = await storageService.getTemplate(id);

    if (!remoteData || !remoteData.content) {
      throw new Error("Template not found or invalid data");
    }

    // Parse the content
    const templateData = JSON.parse(remoteData.content);

    // Deserialize and apply to chart
    const success = deserializeTemplate(templateData, chartStore, chartId);

    if (success) {
      logger.workspace.success(
        `Template "${remoteData.name}" aplicado ao gráfico`,
        {
          name: remoteData.name,
          id: id,
          chartId: chartId,
        },
      );
    }

    return success;
  } catch (error) {
    console.error("Failed to load template remotely:", error);
    logger.workspace.error("Falha ao carregar template do servidor", {
      error: error.message,
      id: id,
    });
    throw error;
  }
};

/**
 * List all templates from remote storage
 * @returns {Promise<Array>} - Array of template metadata
 */
export const listTemplatesRemote = async () => {
  try {
    console.log("Fetching template list from server...");

    const templateList = await storageService.getTemplateList();

    if (!templateList || !Array.isArray(templateList)) {
      console.warn("Invalid template list received from server");
      return [];
    }

    // Transform the data to a consistent format
    const transformedList = templateList.map((template) => ({
      id: template.chartid,
      name: template.name || "Unnamed Template",
      timestamp:
        template.timestamp || template.created || new Date().toISOString(),
      // Parse content to get additional metadata if available
      metadata: (() => {
        try {
          if (template.content) {
            const parsed = JSON.parse(template.content);
            return {
              version: parsed.version,
              chartType: parsed.chartType,
              indicatorsCount: parsed.indicators?.length || 0,
              sourceSymbol: parsed.metadata?.sourceSymbol,
              sourcePeriodicity: parsed.metadata?.sourcePeriodicity,
            };
          }
        } catch {
          // Ignore parse errors
        }
        return {};
      })(),
    }));

    console.log(`Fetched ${transformedList.length} templates from server`);

    return transformedList;
  } catch (error) {
    console.error("Failed to list templates:", error);
    logger.workspace.error("Falha ao listar templates do servidor", {
      error: error.message,
    });
    throw error;
  }
};

/**
 * Delete a template from remote storage
 * @param {string} id - Template ID
 * @returns {Promise<boolean>} - Success status
 */
export const deleteTemplateRemote = async (id) => {
  try {
    if (!id) {
      throw new Error("Template ID is required");
    }

    console.log("Deleting template from remote:", id);

    await storageService.deleteTemplate(id);

    logger.workspace.success("Template removido do servidor", {
      id: id,
    });

    return true;
  } catch (error) {
    console.error("Failed to delete template:", error);
    logger.workspace.error("Falha ao remover template do servidor", {
      error: error.message,
      id: id,
    });
    throw error;
  }
};

/**
 * Rename a template in remote storage
 * @param {string} id - Template ID
 * @param {string} newName - New template name
 * @returns {Promise<boolean>} - Success status
 */
export const renameTemplateRemote = async (id, newName) => {
  try {
    if (!id) {
      throw new Error("Template ID is required");
    }

    if (!newName || newName.trim() === "") {
      throw new Error("New template name is required");
    }

    console.log("Renaming template:", id, "to", newName);

    // Get the existing template
    const existingData = await storageService.getTemplate(id);

    if (!existingData || !existingData.content) {
      throw new Error("Template not found");
    }

    // Save with the new name but same content
    await storageService.saveTemplate(id, newName.trim(), existingData.content);

    logger.workspace.success(`Template renomeado para "${newName}"`, {
      id: id,
      newName: newName.trim(),
    });

    return true;
  } catch (error) {
    console.error("Failed to rename template:", error);
    logger.workspace.error("Falha ao renomear template", {
      error: error.message,
      id: id,
      newName: newName,
    });
    throw error;
  }
};

/**
 * Duplicate a template in remote storage
 * @param {string} id - Template ID to duplicate
 * @param {string} newName - Name for the duplicated template
 * @returns {Promise<Object>} - Duplicated template data with new id
 */
export const duplicateTemplateRemote = async (id, newName) => {
  try {
    if (!id) {
      throw new Error("Template ID is required");
    }

    if (!newName || newName.trim() === "") {
      throw new Error("New template name is required");
    }

    console.log("Duplicating template:", id, "as", newName);

    // Get the existing template
    const existingData = await storageService.getTemplate(id);

    if (!existingData || !existingData.content) {
      throw new Error("Template not found");
    }

    // Save as a new template (-1 means create new)
    const savedData = await storageService.saveTemplate(
      -1,
      newName.trim(),
      existingData.content,
    );

    console.log("Duplicate response from server:", savedData);

    logger.workspace.success(`Template duplicado como "${newName}"`, {
      originalId: id,
      newId: savedData?.chartid || savedData,
      newName: newName.trim(),
    });

    return {
      id: savedData?.chartid,
      name: newName.trim(),
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error("Failed to duplicate template:", error);
    logger.workspace.error("Falha ao duplicar template", {
      error: error.message,
      id: id,
      newName: newName,
    });
    throw error;
  }
};

/**
 * Check if a template name already exists
 * @param {string} name - Template name to check
 * @param {string} excludeId - Optional ID to exclude from check (for rename operations)
 * @returns {Promise<Object|null>} - Template object if exists, null otherwise
 */
export const templateNameExists = async (name, excludeId = null) => {
  try {
    const templateList = await listTemplatesRemote();

    const found = templateList.find(
      (template) =>
        template.name.toLowerCase() === name.toLowerCase() &&
        template.id !== excludeId,
    );

    return found || null;
  } catch (error) {
    console.error("Failed to check template name:", error);
    // Return null on error to allow operation to proceed
    return null;
  }
};
